/**
 * Shared types, constants, and hooks used by both OrderDetailPanel and
 * VisitDetailPanel. Neither panel renders this file directly — they import
 * from it and extend/compose their own UI on top.
 */

import { useMemo } from 'react';
import type { TaskStatus } from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';
import { EDITABLE_STATUSES, SIGNABLE_STATUSES } from '../../constants/statuses';
import { PermissionSlug } from '../../constants/featureFlags';

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export const formatDate = (dateString: string | null | undefined): string => {
  const parsed = parseISOToLocalDate(dateString);
  if (!parsed) return 'N/A';
  return parsed.toLocaleDateString();
};

/**
 * Label for the sign button, named by where the signature sends the chart.
 *
 * Driven by the resolved target rather than the chart's current status,
 * because `reopened` is signable down two lanes: its assigned clinician
 * re-signs it as a draft, while a case manager applies the QA approval and
 * sends it back for the clinician's signature. Labelling that second case
 * "Submit" would name the wrong direction as well as the wrong act.
 */
export function getSignButtonLabel(target: TaskStatus | null): string {
  switch (target) {
    case 'pending_clinician_signature':
      // The QA approval: signed off, and now waiting on the clinician. Named
      // for the act rather than "Return for Review", which would collide with
      // the Returned for Review status - that one means the opposite, a chart
      // handed back *without* approval.
      return 'Approve & Return for Signature';
    case 'completed':
      return 'Sign & Complete';
    default:
      return 'Sign & Submit';
  }
}

// ---------------------------------------------------------------------------
// useTaskPermissions hook
//
// Computes role-based permission flags for sign/approve/break actions.
// Both panels have identical logic — centralised here so changes stay in sync.
// ---------------------------------------------------------------------------

interface UserClinic {
  clinic_id: string;
  is_assignable_clinician: boolean;
}

interface User {
  id: string;
  /** Flat agency permission slugs from /auth/me. */
  permissions?: string[];
  user_clinics?: UserClinic[];
}

interface TaskLike {
  status: TaskStatus;
  agency_clinic_id?: string | null;
  assigned_clinician_user_id?: string | null;
}

/**
 * Agency opt-in for the unsigned moves. Passed in rather than read
 * from a store here so this hook stays a pure function of (user, task, config).
 */
export interface UnsignedMoveConfig {
  allowSubmitWithoutSignature?: boolean;
}

export interface TaskPermissions {
  hasClinicianRole: boolean;
  /**
   * Holds `tasks:approve`: may record the QA signature. Being selectable as a
   * Case Manager grants none of the QA actions; each has its own
   * agency permission, mirrored here from the backend's task_signing gates.
   */
  canQaApprove: boolean;
  /** Holds `tasks:reopen`: may unlock or reopen somebody else's signed chart. */
  canReopenSigned: boolean;
  /**
   * The specific clinician this task is assigned to, not merely anyone holding
   * the clinician role at the clinic. Exposed because some authority questions
   * are status-independent and so cannot be answered by `canBreakSignatures`,
   * which only reports the statuses `/break-signatures` itself accepts.
   */
  isAssignedClinician: boolean;
  canSign: boolean;
  canApprove: boolean;
  canBreakSignatures: boolean;
  /**
   * Whether the pending `/sign` would record a *clinician* signature, which is
   * the only signature that takes the e-signature password. Mirrors the
   * backend's resolve_sign_plan so the dialog asks for a password exactly when
   * the server will demand one.
   */
  signRequiresESignature: boolean;
  /**
   * The status the pending `/sign` would move the task to, or null when there
   * is no task or its status is not signable at all. It does not narrow to null
   * for a user who merely lacks the right to sign — that is what `canSign`
   * answers. Mirrors the backend's SignPlan.target_status, and is
   * what {@link getSignButtonLabel} names for the user: the same status can be
   * signed down two different lanes, so the button cannot be labelled from the
   * *current* status alone.
   */
  signTargetStatus: TaskStatus | null;
  canBypassSignature: boolean;
  canSubmitWithoutSignature: boolean;
  canReturnToClinician: boolean;
}

export function useTaskPermissions(
  user: User | null | undefined,
  task: TaskLike | null | undefined,
  config: UnsignedMoveConfig = {}
): TaskPermissions {
  const allowUnsigned = Boolean(config.allowSubmitWithoutSignature);
  const hasClinicianRole = useMemo(() => {
    if (!user?.user_clinics?.length || !task?.agency_clinic_id) return false;
    return user.user_clinics.some(
      uc => uc.clinic_id === task.agency_clinic_id && uc.is_assignable_clinician
    );
  }, [user?.user_clinics, task?.agency_clinic_id]);

  // The QA-side actions follow agency permissions, not the clinic's
  // assignable-case-manager role. Unlike the store's hasPermission, a super
  // user gets no shortcut: the backend refuses a QA signature without a real
  // role grant, so the controls need the slug itself.
  const permissions = user?.permissions;
  const qa = useMemo(() => {
    const held = permissions ?? [];
    const holds = (slug: string) => held.includes(slug);
    return {
      approve: holds(PermissionSlug.TASKS_APPROVE),
      returnToClinician: holds(PermissionSlug.TASKS_RETURN_TO_CLINICIAN),
      bypass: holds(PermissionSlug.TASKS_BYPASS_CLINICIAN_SIGNATURE),
      reopen: holds(PermissionSlug.TASKS_REOPEN),
    };
  }, [permissions]);
  const canQaApprove = qa.approve;
  const canReopenSigned = qa.reopen;

  // The specific clinician the task is assigned to (not merely anyone holding the
  // clinician role at the clinic). The backend gates signing and breaking on this
  // exact identity, so the UI must too.
  const isAssignedClinician = useMemo(
    () =>
      Boolean(
        user?.id &&
        task?.assigned_clinician_user_id &&
        user.id === task.assigned_clinician_user_id
      ),
    [user?.id, task?.assigned_clinician_user_id]
  );

  // Whether this user can sign, and which of the two signatures it would be,
  // resolved together. They are one decision (the backend makes it in a single
  // resolve_sign_plan), and splitting them into two independent memos is how
  // they would drift: a new QA-signable status added to one and not the other
  // would leave the dialog demanding a password the server never asks for.
  const signPlan = useMemo((): {
    canSign: boolean;
    requiresESignature: boolean;
    targetStatus: TaskStatus | null;
  } => {
    const NO_SIGN = {
      canSign: false,
      requiresESignature: false,
      targetStatus: null,
    };
    if (!user?.id || !task) return NO_SIGN;
    if (!SIGNABLE_STATUSES.has(task.status)) return NO_SIGN;

    // A submitted chart is waiting on the QA signature, which takes
    // tasks:approve.
    if (task.status === 'submitted') {
      return {
        canSign: canQaApprove,
        requiresESignature: false,
        targetStatus: 'pending_clinician_signature',
      };
    }
    // A returned chart is its assigned clinician's alone: no QA
    // lane and no clinic-role fallback, because /sign refuses everyone else
    // (resolve_sign_plan). A chart with no assignee needs reassigning.
    if (task.status === 'returned_for_review') {
      return {
        canSign: isAssignedClinician,
        requiresESignature: true,
        targetStatus: 'submitted_with_signature',
      };
    }
    // A saved/reopened chart can go either way: its assigned clinician signs it
    // as a fresh draft, or a QA signs it back to the clinician for signature.
    // Authorship wins, and only the clinician's own signature takes a password.
    if (task.status === 'saved' || task.status === 'reopened') {
      return {
        canSign: isAssignedClinician || canQaApprove,
        // Load-bearing for the dual-role case: someone who is both the
        // assigned clinician and a QA approver signs as the clinician, so
        // they still owe the password. Do not simplify this to
        // `!canQaApprove`.
        requiresESignature: isAssignedClinician,
        targetStatus: isAssignedClinician
          ? 'submitted_with_signature'
          : 'pending_clinician_signature',
      };
    }
    // Everything else is the clinician's own attestation to the content, which
    // always takes the password. Any future QA-signable status belongs above
    // this line, alongside its own requiresESignature answer.
    // `pending_clinician_signature` falls through here on purpose: it is the
    // clinician co-signing after QA, so the generic clinician branch is exactly
    // right for it — and that co-signature is the one that completes the chart.
    const clinicianTargetStatus: TaskStatus =
      task.status === 'pending_clinician_signature'
        ? 'completed'
        : 'submitted_with_signature';
    if (task.assigned_clinician_user_id) {
      return {
        canSign: user.id === task.assigned_clinician_user_id,
        requiresESignature: true,
        targetStatus: clinicianTargetStatus,
      };
    }
    return {
      canSign: hasClinicianRole,
      requiresESignature: true,
      targetStatus: clinicianTargetStatus,
    };
  }, [
    task?.status,
    task?.assigned_clinician_user_id,
    user?.id,
    hasClinicianRole,
    isAssignedClinician,
    canQaApprove,
  ]);

  const canSign = signPlan.canSign;
  const signRequiresESignature = signPlan.requiresESignature;
  const signTargetStatus = signPlan.targetStatus;

  const canApprove = useMemo(() => {
    if (!user?.id || !task) return false;
    if (task.status !== 'submitted_with_signature') return false;
    return canQaApprove;
  }, [task?.status, user?.id, canQaApprove]);

  const canBreakSignatures = useMemo(() => {
    if (!user?.id || !task) return false;
    // Mirrors backend break_task_signatures: the assigned clinician or anyone
    // holding tasks:reopen can unlock from any signed-but-not-complete
    // state (submitted_with_signature / pending_clinician_signature) and can
    // reopen a finished chart (completed / exported).
    if (
      task.status === 'submitted_with_signature' ||
      task.status === 'pending_clinician_signature' ||
      task.status === 'completed' ||
      task.status === 'exported'
    ) {
      return canReopenSigned || isAssignedClinician;
    }
    return false;
  }, [task?.status, user?.id, canReopenSigned, isAssignedClinician]);

  // Mirrors backend bypass_clinician_signature. Three conditions,
  // all load-bearing:
  //   - only from pending_clinician_signature, the one status where an absent
  //     clinician blocks an otherwise-ready chart;
  //   - holders of tasks:bypass-clinician-signature only;
  //   - never the assigned clinician, who can supply the signature by signing.
  const canBypassSignature = useMemo(() => {
    if (!user?.id || !task) return false;
    if (task.status !== 'pending_clinician_signature') return false;
    if (isAssignedClinician) return false;
    return qa.bypass;
  }, [task?.status, user?.id, qa.bypass, isAssignedClinician]);
  // deferrals. Mirrors submit_task_without_signature: the assigned
  // clinician, from a draft status, and only where the agency opted in.
  const canSubmitWithoutSignature = useMemo(() => {
    if (!allowUnsigned || !user?.id || !task) return false;
    if (!EDITABLE_STATUSES.has(task.status)) return false;
    // `submitted` is editable but is already past this door.
    if (task.status === 'submitted') return false;
    return isAssignedClinician;
  }, [allowUnsigned, task?.status, user?.id, isAssignedClinician]);

  // Mirrors return_task_to_clinician: a QA reviewer (tasks:return-to-clinician)
  // handing work back rather than approving it. A reopened chart waits in the QA queue, so it can be
  // handed back too.
  const canReturnToClinician = useMemo(() => {
    if (!allowUnsigned || !user?.id || !task) return false;
    if (
      task.status !== 'submitted_with_signature' &&
      task.status !== 'submitted' &&
      task.status !== 'reopened'
    ) {
      return false;
    }
    return qa.returnToClinician;
  }, [allowUnsigned, task?.status, user?.id, qa.returnToClinician]);

  return {
    hasClinicianRole,
    canQaApprove,
    canReopenSigned,
    isAssignedClinician,
    canSign,
    canApprove,
    canBreakSignatures,
    signRequiresESignature,
    signTargetStatus,
    canBypassSignature,
    canSubmitWithoutSignature,
    canReturnToClinician,
  };
}
